import { Alternative } from './Alternative';
import { Question } from './Question';

/**
 Class representing a result summary
 */
export class ResultSummary {
    /**
     * Create a result summary.
     * @param {Array<Question>} questions - An array of all questions
     * @param {Number} numberOfCompletedAnswers - The total number of completed (submitted) answers,
     * @param {Array<QuestionResultSummary>} questionResults - The question id.
     */
    constructor(questions, numberOfCompletedAnswers, questionResults) {
        this.numberOfCompletedAnswers = numberOfCompletedAnswers;
        this.questionResults = questions.map(q => new QuestionResultSummary(q, questionResults.find(r => r.questionId === q.questionId)));
    }
}

/**
 Class representing a question result (appending information to existing objects)
 */
export class QuestionResultSummary extends Question {
    /**
     * Create a question result summary.
     * @param {Question} question - The question
     * @param {Object} questionResult - question result as retrieved from api
     * @param {Number} questionResult.questionId - question id which result belongs to (same as question.questionId)
     * @param {Number} questionResult.numberOfAnswers - number of answers given for this question
     * @param {Number} questionResult.yes - number of 'yes' answers for a 'YesNo' question
     * @param {Number} questionResult.no - number of 'no' answers for a 'YesNo' question
     * @param {Array<string>} questionResult.texts - text answers for 'Open' question
     * @param {Array<Object>} questionResult.alternatives - array of alternative answers for 'MultipleChoice' question
     * @param {Number} questionResult.alternatives.alternativeId - alternativeId for an alternative
     * @param {Number} questionResult.alternatives.count - number of answers for the given alternative
     * @param {Array<Object>} questionResult.matrixRows - array of matrix answers for 'Matrix' question
     * @param {Number} questionResult.matrixRows.matrixRowId - the matrix row id
     * @param {Number} questionResult.matrixRows.numberOfAnswers - number of answers for the given matrix row
     * @param {Number} questionResult.matrixRows.alternatives - selected alternatives for the matrix row
     * @param {Number} questionResult.matrixRows.alternatives.alternativeId - alternativeId for the matrix question
     * @param {Number} questionResult.matrixRows.alternatives.count - number of answers for the given alternative for the given row
     */
    constructor(question, questionResult) {
        super(question);
        this.numberOfAnswers = questionResult && questionResult.numberOfAnswers ? questionResult.numberOfAnswers : 0;
        this.texts = questionResult && questionResult.texts ? questionResult.texts : [];
        this.numberOfYesAnswers = questionResult && questionResult.yes ? questionResult.yes : 0;
        this.numberOfNoAnswers = questionResult && questionResult.no ? questionResult.no : 0;

        for (let i = 0; i < this.alternatives.length; i++) {
            const alt = this.alternatives[i];

            alt.numberOfAnswers = 0;

            if (questionResult && questionResult.alternatives) {
                const altRes = questionResult.alternatives.find(r => r.alternativeId === alt.alternativeId);

                alt.numberOfAnswers = altRes && altRes.count ? altRes.count : 0;
            }
        }

        for (let i = 0; i < this.matrixRows.length; i++) {
            const row = this.matrixRows[i];

            row.numberOfAnswers = 0;
            // we add all alternatives to every matrix
            row.alternatives = question.alternatives.map(a => {
                const altRes = new Alternative(a);

                altRes.numberOfAnswers = 0;

                return altRes;
            });

            if (questionResult && questionResult.matrixRows) {
                const rowResult = questionResult.matrixRows.find(r => r.matrixRowId === row.matrixRowId);

                if (rowResult) {
                    row.numberOfAnswers = rowResult.numberOfAnswers ? rowResult.numberOfAnswers : 0;

                    if (rowResult.alternatives) {
                        for (let j = 0; j < row.alternatives.length; j++) {
                            const alt = row.alternatives[j];
                            const altRes = rowResult.alternatives.find(r => r.alternativeId === alt.alternativeId);

                            if (altRes) {
                                alt.numberOfAnswers = altRes.count ? altRes.count : 0;
                            }
                        }
                    }
                }
            }
        }
    }
}
